import { getLastDayOfMonth } from 'commons/utils/date'
import {
  Alert,
  BatchSearch,
  DatePicker,
  Icon,
  LoadingModal,
  Page,
  Select,
  SlimButton
} from 'components'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as S from './styles'
import moment from 'moment'
import {
  allSectorOptions,
  allFactoriesFilter,
  organizationOptions,
  productionTypeOptions,
  Company,
  companies
} from 'constants/enums'
import { ExportStockReport } from 'services/stock/interface'
import { useEffect, useState } from 'react'
import { getUserEmail } from 'commons/utils/user'
import { postStockReport } from 'services/stock'
import { TypesValidations } from 'constants/enums'
import { useCommercialStructure } from 'commons/customHooks'

export const StockReport = () => {
  const { getMarkets } = useCommercialStructure()

  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState<boolean>()

  const markets = getMarkets()
  const marketOptions = markets.map((market) => ({
    title: market.description,
    value: market.id
  }))

  const defaultFields: {
    empresa: string
    organizacao: string
    canal: string
    setor: string
    centro: string
    foraDeLinha: string
    dataFinalFaturamento: string
    materiais: string
    emailUsuario: string
  } = {
    empresa: Company.ALPA,
    organizacao: '',
    canal: '',
    setor: '',
    centro: '',
    foraDeLinha: '',
    dataFinalFaturamento: '',
    materiais: '',
    emailUsuario: ''
  }

  const empresa = [
    {
      title: companies[Company.ALPA].title,
      value: companies[Company.ALPA].value
    }
  ]

  const methods = useForm({
    defaultValues: defaultFields
  })

  const { handleSubmit, watch, getValues } = methods

  const watchFields = watch()
  const watchRequiredFields = watch([
    'organizacao',
    'canal',
    'setor',
    'centro',
    'foraDeLinha',
    'dataFinalFaturamento'
  ])
  const isDisabled = watchRequiredFields.some((field) => !field)

  useEffect(() => {
    if (hasError !== undefined) {
      setHasError(undefined)
    }
  }, [JSON.stringify(watchFields)])

  const requestStockReport = async () => {
    const {
      empresa,
      organizacao,
      canal,
      setor,
      centro,
      foraDeLinha,
      dataFinalFaturamento,
      materiais
    } = getValues()

    const requestBody = {
      Empresa: empresa,
      Organizacao: organizacao,
      Canal: canal,
      Setor: setor,
      Centro: centro,
      ForaDeLinha: foraDeLinha == TypesValidations.YES,
      DataFinalFaturamento: getLastDayOfMonth(
        dataFinalFaturamento,
        'YYYY-MM-DDTHH:mm:ss'
      ) as unknown as string,
      Materiais: materiais ? materiais.split(',').filter(Boolean) : [],
      EmailUsuario: getUserEmail()
    }

    try {
      setLoading(true)
      await postStockReport(requestBody as ExportStockReport)

      setHasError(false)
    } catch (error) {
      setHasError(true)
    } finally {
      setLoading(false)
    }
  }

  const renderAlert = () => {
    if (hasError === undefined) {
      return null
    }

    const kind = hasError ? 'error' : 'success'
    const title = hasError
      ? 'Ocorreu um erro ao tentar registrar sua solicitação.'
      : 'Solicitação registrada com sucesso!'
    const message = hasError
      ? 'Tente realizar o registro da sua solicitação novamente em alguns minutos.'
      : 'A planilha solicitada será enviada para o seu email cadastrado.'

    return <Alert fullWidth kind={kind} title={title} message={message} />
  }

  return (
    <>
      {loading && <LoadingModal />}
      <Page fullwidth>
        <FormProvider {...methods}>
          <S.Form>
            <S.Title>Solicitação de relatório de estoque / reserva</S.Title>
            <S.FieldsContainer>
              <S.FieldWrapper
                templateColumns="1fr 1fr 1fr 1fr"
                mediaTemplateColumns="1fr 1fr"
              >
                <S.FieldItemWrapper>
                  <Controller
                    name="empresa"
                    defaultValue={Company.ALPA}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Empresa"
                        disabled={true}
                        value={value}
                        id="empresa"
                        name={name}
                        options={empresa}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="organizacao"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Organização"
                        value={value}
                        id="organizacao"
                        name={name}
                        options={organizationOptions}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="canal"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Mercado"
                        value={value}
                        id="canal"
                        name={name}
                        options={marketOptions}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="setor"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Setor"
                        value={value}
                        id="setor"
                        name={name}
                        options={allSectorOptions}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
              </S.FieldWrapper>
              <S.FieldWrapper
                templateColumns="1fr 1fr 1fr 1fr"
                mediaTemplateColumns="1fr 1fr"
              >
                <S.FieldItemWrapper>
                  <Controller
                    name="centro"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Fábrica"
                        value={value}
                        id="centro"
                        name={name}
                        options={allFactoriesFilter}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="foraDeLinha"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Status do material"
                        value={value}
                        id="foraDeLinha"
                        name={name}
                        options={productionTypeOptions}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <S.LabelFilter>Data de Faturamento</S.LabelFilter>
                  <Controller
                    name="dataFinalFaturamento"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        minDate={moment() as unknown as string}
                        maxDate={moment().add(1, 'month') as unknown as string}
                        error={fieldState.error?.message}
                        views={['year', 'month']}
                        inputFormat="MM/YYYY"
                        placeholder="mm/aaaa"
                        disableMaskedInput={true}
                        height={30}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <S.LabelFilter>Materiais específicos</S.LabelFilter>
                  <Controller
                    name="materiais"
                    render={({ field }) => (
                      <BatchSearch
                        placeHolder="Buscar por códigos (Ex: 4000029)"
                        height={30}
                        value={field.value || ''}
                        onChange={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
              </S.FieldWrapper>
            </S.FieldsContainer>

            <S.ActionsContainer>
              <SlimButton
                id="request-button"
                content="Solicitar"
                type="button"
                variant="primary"
                icon={<Icon icon="search" size="15" />}
                disabled={isDisabled || loading}
                loading={loading}
                onClick={handleSubmit(requestStockReport)}
              />
            </S.ActionsContainer>
          </S.Form>
        </FormProvider>

        {renderAlert()}
      </Page>
    </>
  )
}
